import { useNavigate } from "react-router-dom";
import BreadCumb from "../../components/common/Breadcrumb";
import backArrow from "../../assets/task/back_arrow.svg";
import { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { ALL_EMPLOYEE_CLOCK_INS_BY_AGENCY_ID, EXPORT_ATTTENDANCE_TO_EXCEL } from "../../api/query";
import { useAuth } from "../../context/AuthContext";
import Loader from "../../components/common/Loader";
import { endOfMonth, format, startOfMonth } from "date-fns";

interface AttendanceRecord {
    record_date: string;
    week_day: string;
    check_in_time: string | null;
    check_out_time: string | null;
    is_present: boolean;
    is_leave: boolean;
    is_holiday: boolean;
    is_absent: boolean;
    is_weekend: boolean;
    hours?: number;
    minutes?: number;
}

interface AggregatedDayRecord {
    record_date: string;
    week_day: string;
    is_present: boolean;
    is_leave: boolean;
    is_holiday: boolean;
    is_absent: boolean;
    is_weekend: boolean;
    total_minutes: number;
    records: AttendanceRecord[];
}

interface EmployeeRecord {
    employee_id: number;
    employee_name: string;
    attendance: AttendanceRecord[];
}

type AttendanceSymbol = "P" | "A" | "L" | "W" | "H" | "-";

const AllAttendance = () => {
    const navigate = useNavigate();

    const { userInfo } = useAuth()
    const agencyID = userInfo?.agencies?.id || '';

    const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);

    const daysInMonth = new Date(year, month, 0).getDate();

    //api
    const [
        getAttendance,
        { data, loading },
    ] = useLazyQuery(ALL_EMPLOYEE_CLOCK_INS_BY_AGENCY_ID, {
        fetchPolicy: 'no-cache'
    });

    const [
        exportAttendance,
        { loading: exportLoading },
    ] = useLazyQuery(EXPORT_ATTTENDANCE_TO_EXCEL, {
        fetchPolicy: 'no-cache'
    });

    useEffect(() => {
        if (agencyID && month && year) {
            const firstDayOfMonth = startOfMonth(new Date(year, month - 1));
            const lastDayOfMonth = endOfMonth(new Date(year, month - 1));

            const formattedStartDate = format(firstDayOfMonth, 'yyyy-MM-dd');
            const formattedEndDate = format(lastDayOfMonth, 'yyyy-MM-dd'
            );

            getAttendance({
                variables: {
                    agency_id: parseInt(agencyID),
                    start_date: formattedStartDate,
                    end_date: formattedEndDate,
                },
            });
        }
    }, [agencyID, month, year])

    useEffect(() => {
        if (data && data.all_employee_clock_ins_by_agency_id?.status_code == 200) {
            setEmployees(data.all_employee_clock_ins_by_agency_id.records || []);
        }
    }, [data])

    // Map flags → status letter for aggregated records
    const getStatus = (a: AggregatedDayRecord): AttendanceSymbol => {
        if (a.is_present) return "P";
        if (a.is_leave) return "L";
        if (a.is_holiday) return "H";
        if (a.is_weekend) return "W";
        if (a.is_absent) return "A";
        return "A";
    };

    // Check if a date is in the future
    const isFutureDate = (day: number): boolean => {
        const currentDate = new Date();
        const checkDate = new Date(year, month - 1, day);
        return checkDate > currentDate;
    };

    // Colors for status badges
    const getStatusColor = (status: AttendanceSymbol) => {
        switch (status) {
            case "P":
                return "present-status_dv";
            case "A":
                return "absent_blg_dv";
            case "L":
                return "leave_blg_dv";
            case "H":
                return "holiday-status_dv";
            case "W":
                return "weekend-status_dv";
            case "-":
                return "bg-gray-200 text-gray-500";
            default:
                return "";
        }
    };

    // Handle row selection
    const handleRowClick = (employeeId: number) => {
        setSelectedEmployeeId(selectedEmployeeId === employeeId ? null : employeeId);
    };

    // Calculate working minutes from a single attendance record
    const calculateWorkingMinutes = (record: AttendanceRecord): number => {
        if (!record.is_present || !record.check_in_time || !record.check_out_time) {
            return 0;
        }

        // If hours and minutes are provided directly
        if (record.hours !== undefined && record.minutes !== undefined) {
            return (record.hours * 60) + record.minutes;
        }

        // Calculate from check-in and check-out times (they come as full datetime strings)
        try {
            const checkIn = new Date(record.check_in_time);
            const checkOut = new Date(record.check_out_time);

            if (isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) {
                return 0;
            }

            const diffMs = checkOut.getTime() - checkIn.getTime();
            return Math.floor(diffMs / (1000 * 60));
        } catch (error) {
            return 0;
        }
    };

    // Convert minutes to hours and minutes string
    const formatWorkingHours = (totalMinutes: number): string => {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}h ${minutes}m`;
    };

    // Aggregate multiple records for the same date
    const aggregateAttendanceByDate = (attendance: AttendanceRecord[]): AggregatedDayRecord[] => {
        const dateMap = new Map<string, AggregatedDayRecord>();

        attendance.forEach((record) => {
            const dateKey = record.record_date;

            if (!dateMap.has(dateKey)) {
                // Create new aggregated record
                dateMap.set(dateKey, {
                    record_date: record.record_date,
                    week_day: record.week_day,
                    is_present: false,
                    is_leave: record.is_leave,
                    is_holiday: record.is_holiday,
                    is_absent: true, // Default to absent, will be updated if any present record found
                    is_weekend: record.is_weekend,
                    total_minutes: 0,
                    records: []
                });
            }

            const aggregated = dateMap.get(dateKey)!;
            aggregated.records.push(record);

            // If any record shows present, mark the day as present
            if (record.is_present) {
                aggregated.is_present = true;
                aggregated.is_absent = false;
                aggregated.total_minutes += calculateWorkingMinutes(record);
            }

            // Update other flags (take the most recent or combine logically)
            aggregated.is_leave = aggregated.is_leave || record.is_leave;
            aggregated.is_holiday = aggregated.is_holiday || record.is_holiday;
            aggregated.is_weekend = aggregated.is_weekend || record.is_weekend;
        });

        return Array.from(dateMap.values());
    };

    // Calculate total working hours for an employee from aggregated records
    const calculateTotalWorkingHours = (aggregatedRecords: AggregatedDayRecord[]): string => {
        const totalMinutes = aggregatedRecords.reduce((sum, record) => {
            return sum + record.total_minutes;
        }, 0);

        return formatWorkingHours(totalMinutes);
    };

    const handleExportAttendance = async () => {
        if (!agencyID || !month || !year) return;

        const firstDayOfMonth = startOfMonth(new Date(year, month - 1));
        const lastDayOfMonth = endOfMonth(new Date(year, month - 1));

        const formattedStartDate = format(firstDayOfMonth, 'yyyy-MM-dd');
        const formattedEndDate = format(lastDayOfMonth, 'yyyy-MM-dd');

        try {
            const result = await exportAttendance({
                variables: {
                    input: {
                        agency_id: parseInt(agencyID),
                        start_date: formattedStartDate,
                        end_date: formattedEndDate,
                    },
                },
            });

            if (result.data?.export_attendance_to_excel?.status_code === 200) {
                const fileUrl = result.data.export_attendance_to_excel.file_url;
                const filename = result.data.export_attendance_to_excel.filename;

                // Create a temporary link to download the file
                const link = document.createElement('a');
                link.href = fileUrl;
                link.download = filename || 'attendance_export.xlsx';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                console.error('Export failed:', result.data?.export_attendance_to_excel?.message);
            }
        } catch (error) {
            console.error('Export error:', error);
        }
    };

    return (
        <>
            <div id="cts-respon-p-dv" className="w-full flex flex-col items-start justify-start py-40 px-20  mq575:px-5 box-border gap-[20px] max-w-[100%] mq1425:max-w-full">
                <div className="min-w-full flex flex-col items-start justify-start gap-6">
                    <BreadCumb />

                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">Attendance</h3>
                    </div>
                    <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
                        <div className="grid gap-[24px] w-full">
                            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full mt-[8px]">
                                <select
                                    value={month}
                                    onChange={(e) => setMonth(Number(e.target.value))}
                                    className="border rounded p-2 select-dropdown-ui"
                                >
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                        <option key={m} value={m}>
                                            {new Date(0, m - 1).toLocaleString("default", { month: "long" })}
                                        </option>
                                    ))}
                                </select>

                                <select
                                    value={year}
                                    onChange={(e) => setYear(Number(e.target.value))}
                                    className="border rounded p-2 select-dropdown-ui"
                                >
                                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                                        <option key={y} value={y}>
                                            {y}
                                        </option>
                                    ))}
                                </select>

                                <button
                                    onClick={handleExportAttendance}
                                    disabled={exportLoading || !employees.length}
                                    className="export-btn disabled:bg-gray-400 w-fit text-white px-4 py-2 rounded flex items-center gap-2 transition-colors"
                                >
                                    {exportLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Exporting...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Export to Excel
                                        </>
                                    )}
                                </button>
                            </div>
                            <div className="allattendce_status_main grid gap-[8px]">
                                <h5 className="flex-1 flex items-center my-0">All Attendance Status</h5>

                                <div className="grid grid-cols-5 mq1400:grid-5 mq1275:grid-cols-5 mq950:mq1275:grid-cols-3 mq575::grid-cols-2 mt-[8px] gap-[8px] w-[791px] ">
                                    <div className="present_blg_dv flex items-center gap-[8px]">
                                        <span className="present-status_dv flex justify-center items-center">P</span><p className="text-sm dark:text-white my-0 text-white">Present</p>
                                    </div>
                                    <div className="present_blg_dv flex items-center gap-[8px]">
                                        <span className="absent_blg_dv flex justify-center items-center">A</span><p className="text-sm dark:text-white my-0 text-white">Absent</p>
                                    </div>
                                    <div className="present_blg_dv flex items-center gap-[8px]">
                                        <span className="leave_blg_dv  flex justify-center items-center">L</span><p className="text-sm dark:text-white my-0 text-white">Leave</p>
                                    </div>
                                    <div className="present_blg_dv flex items-center gap-[8px]">
                                        <span className="weekend-status_dv flex justify-center items-center">W</span><p className="text-sm dark:text-white my-0 text-white">Weekend</p>
                                    </div>
                                    <div className="present_blg_dv flex items-center gap-[8px]">
                                        <span className="holiday-status_dv flex justify-center items-center">H</span><p className="text-sm dark:text-white my-0 text-white">Holiday</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex w-full overflow-x-auto relative">
                                <table className="attendancetbl-blg-dv">
                                    <thead className="sticky top-0 z-20 bg-white">
                                        <tr>
                                            <th className="sticky left-0 bg-white z-30 border-r"><b>Employee</b></th>
                                            {Array.from({ length: daysInMonth }, (_, i) => {
                                                const date = new Date(year, month - 1, i + 1);
                                                const dayName = date.toLocaleString("default", { weekday: "short" }); // Mon, Tue, etc.
                                                return (
                                                    <th key={i + 1} className="p-2 border text-center bg-white">
                                                        <div className="flex flex-col items-center">
                                                            <span className="text-[10px] text-gray-500 dark:text-gray-400">{dayName}</span>
                                                            <span className="font-semibold">{i + 1}</span>
                                                        </div>
                                                    </th>
                                                );
                                            })}
                                            <th className="bg-white"><b>Summary</b></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {employees?.map((emp: EmployeeRecord) => {
                                            // Create map {dayNumber -> record}
                                            // const dayMap = new Map<number, AttendanceRecord>();
                                            // emp.attendance.forEach((rec) => {
                                            //     const day = new Date(rec.record_date).getDate();
                                            //     dayMap.set(day, rec);
                                            // });

                                            // Aggregate attendance records by date (sum multiple records per day)
                                            const aggregatedAttendance = aggregateAttendanceByDate(emp.attendance);

                                            // Build day map using aggregated records
                                            const dayMap = new Map<number, AggregatedDayRecord>();
                                            aggregatedAttendance.forEach((rec) => {
                                                const day = new Date(rec.record_date).getDate();
                                                dayMap.set(day, rec);
                                            });

                                            // Count present days, absent days, and leave days (excluding future dates)
                                            const presentCount = aggregatedAttendance.filter((a) => {
                                                const recordDay = new Date(a.record_date).getDate();
                                                const isNotFuture = !isFutureDate(recordDay);
                                                return a.is_present && isNotFuture;
                                            }).length;
                                            const absentCount = aggregatedAttendance.filter((a) => {
                                                const recordDay = new Date(a.record_date).getDate();
                                                const isNotFuture = !isFutureDate(recordDay);
                                                return a.is_absent && !a.is_weekend && !a.is_holiday && !a.is_leave && isNotFuture;
                                            }).length;
                                            const leaveCount = aggregatedAttendance.filter((a) => {
                                                const recordDay = new Date(a.record_date).getDate();
                                                const isNotFuture = !isFutureDate(recordDay);
                                                return a.is_leave && !a.is_present && isNotFuture;
                                            }).length;
                                            const totalWorkingHours = calculateTotalWorkingHours(aggregatedAttendance);

                                            const isSelected = selectedEmployeeId === emp.employee_id;

                                            return (
                                                <tr
                                                    key={emp.employee_id}
                                                    className={`${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''} hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors`}
                                                >
                                                    <td
                                                        className={`sticky left-0 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white'} z-10 border-r whitespace-nowrap cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-800/30 transition-colors`}
                                                        onClick={() => handleRowClick(emp.employee_id)}
                                                    >
                                                        {emp.employee_name}
                                                    </td>

                                                    {Array.from({ length: daysInMonth }, (_, i) => {
                                                        const dayNumber = i + 1;
                                                        const record = dayMap.get(dayNumber);

                                                        // Check if this is a future date
                                                        const isFuture = isFutureDate(dayNumber);

                                                        // Determine status: show "-" for future dates, otherwise use normal logic
                                                        const status: AttendanceSymbol = isFuture ? "-" : (record ? getStatus(record) : "A");
                                                        const workingHours = record && status === "P" ? formatWorkingHours(record.total_minutes) : "";

                                                        return (
                                                            <td key={dayNumber} className="p-1 border text-center">
                                                                <span
                                                                    className={`inline-block w-6 h-6 rounded ${getStatusColor(
                                                                        status
                                                                    )} text-xs font-bold leading-6 ${status !== "-" ? "cursor-pointer" : ""}`}
                                                                    title={status === "P" && workingHours ? `Working Hours: ${workingHours}` : status === "-" ? "Future date" : ""}
                                                                >
                                                                    {status}
                                                                </span>
                                                            </td>
                                                        );
                                                    })}

                                                    <td className="p-2">
                                                        <div className="flex flex-col gap-[4px]">
                                                            <div className="flex gap-[8px] items-center">
                                                                <span className="summary-status-green">{presentCount}</span>
                                                                <span className="summary-status-red">{absentCount}</span>
                                                                <span className="summary-status-yellow">{leaveCount}</span>
                                                            </div>
                                                            <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                                                                Total: {totalWorkingHours}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {(loading || exportLoading) && (
                <Loader />
            )}
        </>
    )
}

export default AllAttendance; 